import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { CommonHTMLAttributes } from '@frontend/types';
import { useTooltip } from '@frontend/design-system';
import { ThreadAttachedAsset } from '../../types';
import { AttachedAssetChip } from './attached-asset-chip';
import { container, contentWrapper, iconWrapper, assetsList } from './attached-assets-bar.styles';
import { AttachedAssetsOverflowMenu } from './attached-assets-overflow-menu';

type AttachedAssetsBarProps = CommonHTMLAttributes & {
  assets: ThreadAttachedAsset[];

  /** When provided, only the first `maxVisibleAssets` are rendered as chips and the remainder is shown as a "+N" indicator. */
  maxVisibleAssets?: number;
};

export const AttachedAssetsBar = ({ assets, maxVisibleAssets, ...rest }: AttachedAssetsBarProps) => {
  const { t } = useTranslation('integrated-messaging');
  const { Tooltip, tooltipProps, triggerProps } = useTooltip({ placement: 'top' });

  if (!assets.length) return null;

  const visibleAssets = maxVisibleAssets ? assets.slice(0, maxVisibleAssets) : assets;
  const hiddenAssets = assets.slice(visibleAssets.length);
  const overflowCount = hiddenAssets.length;

  return (
    <div className={container} {...rest}>
      <div className={contentWrapper}>
        <span className={iconWrapper} {...triggerProps}>
          <Icon name='link-small' color='light' />
        </span>
        <Tooltip {...tooltipProps}>{t('In Your Link')}</Tooltip>
        <ul aria-label={t('Attachments')} className={assetsList}>
          {visibleAssets.map((asset) => (
            <AttachedAssetChip key={asset.id} asset={asset} />
          ))}
          {overflowCount > 0 && (
            <AttachedAssetsOverflowMenu hiddenAssets={hiddenAssets} overflowCount={overflowCount} />
          )}
        </ul>
      </div>
    </div>
  );
};
